import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, LockOpen, Loader2, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Admin control to lock / unlock a performance year (read-only mode).
 * Lives in Admin → System Settings next to Program Year Settings.
 * Per-user unlock (shared-practice aware) is planned for a later phase; this is
 * the year-level lock/unlock.
 */
export function YearLockingSettings() {
  const { availableYears, operatingYear, isYearLocked, refreshLockedYears } = useProgramYear();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";
  const [busyYear, setBusyYear] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
  });

  const setLock = async (year: number, lock: boolean) => {
    setBusyYear(year);
    try {
      const response = lock
        ? await fetch(`${apiUrl}/api/year-management/lock`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ year, reason: "Locked from Admin → System Settings" }),
          })
        : await fetch(`${apiUrl}/api/year-management/lock/${year}`, {
            method: "DELETE",
            headers: authHeaders(),
          });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Request failed");
      }
      await refreshLockedYears();
      toast({
        title: lock ? `Program year ${year} locked` : `Program year ${year} unlocked`,
        description: lock
          ? "This year is now read-only across the app."
          : "This year is editable again.",
      });
    } catch (error) {
      console.error("Error toggling year lock:", error);
      toast({
        title: "Action failed",
        description: error instanceof Error ? error.message : "Could not update the year lock.",
        variant: "destructive",
      });
    } finally {
      setBusyYear(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Year Locking & Access
        </CardTitle>
        <CardDescription>
          Lock a finalized performance year to make it read-only across the app. Locked years can still be viewed; no one
          can edit, upload, recalculate, or change settings for them until you unlock.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isAdmin && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Only administrators can lock or unlock years.</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          {availableYears.map((year) => {
            const locked = isYearLocked(year);
            const busy = busyYear === year;
            return (
              <div
                key={year}
                className="flex items-center justify-between rounded-md border px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium">{year}</span>
                  {year === operatingYear && (
                    <Badge variant="secondary" className="text-xs">Current</Badge>
                  )}
                  {locked ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Locked (read-only)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-xs">Editable</Badge>
                  )}
                </div>
                <Button
                  variant={locked ? "outline" : "default"}
                  size="sm"
                  disabled={!isAdmin || busy}
                  onClick={() => setLock(year, !locked)}
                >
                  {busy ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : locked ? (
                    <LockOpen className="mr-2 h-4 w-4" />
                  ) : (
                    <Lock className="mr-2 h-4 w-4" />
                  )}
                  {locked ? "Unlock" : "Lock"}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="rounded-lg border">
          <button
            type="button"
            onClick={() => setHelpOpen(o => !o)}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {helpOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            How locking works
          </button>
          {helpOpen && (
            <ul className="px-4 pb-4 text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Locked years are fully viewable but read-only — uploads, manual edits, scoring, settings, and goals are blocked.</li>
              <li>Measure <em>selections</em> stay editable (config flexibility, no CMS-scoring impact).</li>
              <li>Enforced in the app (clear message) and in the database (cannot be bypassed).</li>
              <li>A lock stays until you unlock it here. Per-user / per-practice unlock is coming later.</li>
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
